import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ArrowRight, Calendar, Check, Clock, Download, AlertTriangle, MapPin, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAvailability, createBooking, downloadICS } from '@/lib/scheduler/api';
import type { MeetingType, AvailableSlot, BookingResult, SchedulerStep, BookingFormData } from '@/lib/scheduler/types';
import { format, addDays, startOfDay, isSameDay } from 'date-fns';

interface Props {
  defaultSlug?: string | null;
  onComplete?: () => void;
}

const ROLES = ['CFO', 'CIO', 'CMO', 'COO', 'VP Revenue Cycle', 'Director of Surgery', 'Practice Administrator', 'Surgeon', 'Other'];

export function SchedulerFlow({ defaultSlug, onComplete }: Props) {
  const [step, setStep] = useState<SchedulerStep>(defaultSlug ? 'timezone' : 'meeting-type');
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [selectedType, setSelectedType] = useState<MeetingType | null>(null);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState<BookingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<BookingFormData>({
    firstName: '', lastName: '', email: '', organization: '', role: '', notes: '',
  });

  // Load meeting types
  useEffect(() => {
    supabase
      .from('scheduler_meeting_types')
      .select('*')
      .eq('is_active', true)
      .order('duration_minutes')
      .then(({ data }) => {
        const types = (data || []) as unknown as MeetingType[];
        setMeetingTypes(types);
        if (defaultSlug) {
          const found = types.find(t => t.slug === defaultSlug);
          if (found) setSelectedType(found);
        }
      });
  }, [defaultSlug]);

  // Load availability when type is selected
  useEffect(() => {
    if (!selectedType || step !== 'select-time') return;
    setLoading(true);
    setError(null);
    const start = new Date().toISOString();
    const end = addDays(new Date(), selectedType.booking_window_days).toISOString();
    fetchAvailability(selectedType.slug, timezone, start, end)
      .then(({ slots }) => setSlots(slots))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedType, step, timezone]);

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const map = new Map<string, AvailableSlot[]>();
    for (const slot of slots) {
      const day = format(new Date(slot.time), 'yyyy-MM-dd');
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(slot);
    }
    return map;
  }, [slots]);

  const availableDates = useMemo(() => Array.from(slotsByDate.keys()).sort(), [slotsByDate]);

  const slotsForSelectedDate = useMemo(() => {
    const key = format(selectedDate, 'yyyy-MM-dd');
    return slotsByDate.get(key) || [];
  }, [slotsByDate, selectedDate]);

  // Auto-select first available date
  useEffect(() => {
    if (availableDates.length > 0 && step === 'select-time') {
      setSelectedDate(new Date(availableDates[0]));
    }
  }, [availableDates, step]);

  const handleSubmit = async () => {
    if (!selectedType || !selectedSlot) return;
    setLoading(true);
    setError(null);
    try {
      const isDemo = new URLSearchParams(window.location.search).get('demo') === '1';
      const result = await createBooking({
        meetingTypeSlug: selectedType.slug,
        selectedSlotStart: selectedSlot,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        organization: form.organization || undefined,
        role: form.role || undefined,
        notes: form.notes || undefined,
        timezone,
        demo: isDemo,
      });
      setBooking(result);
      setStep('confirmation');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    const order: SchedulerStep[] = ['meeting-type', 'timezone', 'select-time', 'form'];
    const idx = order.indexOf(step);
    if (idx > 0) setStep(order[idx - 1]);
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-success" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Schedule with LINK</h2>
            <p className="text-xs text-muted-foreground">
              {step === 'meeting-type' && 'Select a meeting type'}
              {step === 'timezone' && 'Confirm your timezone'}
              {step === 'select-time' && 'Choose a time'}
              {step === 'form' && 'Your details'}
              {step === 'confirmation' && 'Confirmed'}
            </p>
          </div>
        </div>

        {/* Progress */}
        {step !== 'confirmation' && (
          <div className="flex gap-1 mt-3">
            {['meeting-type', 'timezone', 'select-time', 'form'].map((s, i) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  ['meeting-type', 'timezone', 'select-time', 'form'].indexOf(step) >= i
                    ? 'bg-success'
                    : 'bg-border'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="px-6 py-5 min-h-[300px]">
        {/* Step 1: Meeting Type */}
        {step === 'meeting-type' && (
          <div className="space-y-3">
            {meetingTypes.length === 0 ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : (
              meetingTypes.map(mt => (
                <button
                  key={mt.id}
                  onClick={() => { setSelectedType(mt); setStep('timezone'); }}
                  className={`w-full text-left p-4 rounded-xl border transition-all hover:border-success/40 hover:bg-success/5 ${
                    selectedType?.id === mt.id ? 'border-success bg-success/5' : 'border-border/50 bg-card/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">{mt.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{mt.description}</p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0 ml-4">
                      <Clock className="w-3 h-3" />
                      {mt.duration_minutes} min
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* Step 2: Timezone */}
        {step === 'timezone' && (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Your Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {[
                    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
                    'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'Europe/London',
                    'Europe/Berlin', 'Asia/Tokyo', 'Australia/Sydney',
                  ].map(tz => (
                    <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ')}
              </p>
            </div>
            {selectedType && (
              <div className="p-3 rounded-lg bg-card/50 border border-border/30">
                <p className="text-sm font-medium text-foreground">{selectedType.title}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedType.duration_minutes} minutes • {selectedType.location_type.replace('_', ' ')}
                </p>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" size="sm" onClick={goBack} className="gap-1">
                <ArrowLeft className="w-3 h-3" /> Back
              </Button>
              <Button size="sm" onClick={() => setStep('select-time')} className="gap-1 bg-success hover:bg-success/90 text-success-foreground">
                Continue <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Select Time */}
        {step === 'select-time' && (
          <div className="space-y-4">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-48" />
                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <Skeleton key={i} className="h-10" />
                  ))}
                </div>
              </div>
            ) : slots.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No available times found. Please try a different meeting type or check back later.
              </p>
            ) : (
              <>
                {/* Date selector */}
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                  {availableDates.slice(0, 14).map(dateStr => {
                    const d = new Date(dateStr);
                    const isSelected = isSameDay(selectedDate, d);
                    return (
                      <button
                        key={dateStr}
                        onClick={() => setSelectedDate(d)}
                        className={`flex flex-col items-center px-3 py-2 rounded-lg border transition-all shrink-0 min-w-[60px] ${
                          isSelected
                            ? 'border-success bg-success/10 text-success'
                            : 'border-border/30 hover:border-success/30 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <span className="text-[10px] uppercase font-medium">{format(d, 'EEE')}</span>
                        <span className="text-lg font-bold">{format(d, 'd')}</span>
                        <span className="text-[10px]">{format(d, 'MMM')}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Time slots */}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[240px] overflow-y-auto">
                  {slotsForSelectedDate.map(slot => {
                    const time = new Date(slot.time);
                    const isSelected = selectedSlot === slot.time;
                    return (
                      <button
                        key={slot.time}
                        onClick={() => setSelectedSlot(slot.time)}
                        className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                          isSelected
                            ? 'border-success bg-success text-success-foreground'
                            : 'border-border/50 hover:border-success/40 text-foreground'
                        }`}
                      >
                        {format(time, 'h:mm a')}
                      </button>
                    );
                  })}
                  {slotsForSelectedDate.length === 0 && (
                    <p className="col-span-full text-sm text-muted-foreground text-center py-4">
                      No slots available on this date.
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" size="sm" onClick={goBack} className="gap-1">
                <ArrowLeft className="w-3 h-3" /> Back
              </Button>
              <Button
                size="sm"
                onClick={() => setStep('form')}
                disabled={!selectedSlot}
                className="gap-1 bg-success hover:bg-success/90 text-success-foreground"
              >
                Continue <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Form */}
        {step === 'form' && (
          <div className="space-y-4">
            {selectedSlot && selectedType && (
              <div className="p-3 rounded-lg bg-card/50 border border-border/30 flex items-center gap-3">
                <Calendar className="w-4 h-4 text-success shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {format(new Date(selectedSlot), 'EEEE, MMMM d, yyyy')} at {format(new Date(selectedSlot), 'h:mm a')}
                  </p>
                  <p className="text-xs text-muted-foreground">{selectedType.title} • {selectedType.duration_minutes} min</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First Name *</Label>
                <Input
                  value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  placeholder="Jane"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Last Name *</Label>
                <Input
                  value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  placeholder="Doe"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane@hospital.org"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Organization</Label>
              <Input
                value={form.organization}
                onChange={e => setForm(f => ({ ...f, organization: e.target.value }))}
                placeholder="Cincinnati General Hospital"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Notes</Label>
              <div className="mt-1 p-2 rounded-md bg-warning/5 border border-warning/20 mb-1.5">
                <p className="text-[10px] text-warning flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Do not include patient identifiers or PHI.
                </p>
              </div>
              <Textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="What would you like to discuss?"
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" size="sm" onClick={goBack} className="gap-1">
                <ArrowLeft className="w-3 h-3" /> Back
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={loading || !form.firstName || !form.lastName || !form.email}
                className="gap-1 bg-success hover:bg-success/90 text-success-foreground"
              >
                {loading ? 'Booking...' : 'Confirm Booking'}
                <Check className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 'confirmation' && booking && (
          <div className="space-y-5 text-center py-4">
            <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-success" />
            </div>

            <div>
              <h3 className="text-lg font-bold text-foreground">Meeting Confirmed</h3>
              <p className="text-sm text-muted-foreground mt-1">
                You'll receive a calendar invite at <span className="font-medium text-foreground">{form.email}</span>
              </p>
            </div>

            <div className="p-4 rounded-xl bg-card/50 border border-border/30 text-left space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-success" />
                <span className="font-medium">
                  {format(new Date(booking.booking.start_time), 'EEEE, MMMM d, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>
                  {format(new Date(booking.booking.start_time), 'h:mm a')} – {format(new Date(booking.booking.end_time), 'h:mm a')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{booking.location}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>Host: {booking.host.display_name}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                onClick={() => downloadICS(booking.icsContent)}
                className="gap-2 bg-success hover:bg-success/90 text-success-foreground"
              >
                <Download className="w-4 h-4" />
                Add to Calendar (.ics)
              </Button>
              <Button variant="outline" size="sm" onClick={onComplete}>
                Close
              </Button>
            </div>

            <p className="text-[10px] text-muted-foreground">
              Need to change this? Use the links in your confirmation email to reschedule or cancel.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
